/**
 * One login (e-mail) may belong to several agencies — one backend `users` row
 * ("membership") per agency. The chosen membership is remembered per e-mail so
 * the next login opens the last used agency directly. It is sent as
 * `x-user-id`; the backend only accepts it if it is one of the caller's own
 * memberships (verified Firebase token), so a stale or foreign id is harmless.
 */
export interface AgencyMembership {
  id: string;
  agencyId: string | null;
  agencyName: string | null;
  agencyLogoUrl: string | null;
  role: string;
}

const key = (email: string) => `activeMembership:${email.trim().toLowerCase()}`;

export function getActiveMembershipId(email?: string | null): string | null {
  if (!email) return null;
  try {
    return localStorage.getItem(key(email));
  } catch {
    return null;
  }
}

export function setActiveMembershipId(email: string, membershipId: string): void {
  try {
    localStorage.setItem(key(email), membershipId);
  } catch {
    // private mode: the picker simply shows again next time
  }
}

/**
 * Browser-stored state that belongs to ONE agency: form drafts (client data!),
 * the agency colour, matching field presets, running AI report jobs. Removed
 * on every agency switch so nothing of agency A shows up in agency B.
 */
const AGENCY_SCOPED_KEYS = ['agencyThemeColor', 'lastMatchingFields', 'userData'];
const AGENCY_SCOPED_PREFIXES = ['form_draft_', 'herzens-ai-report-job:'];

export function clearAgencyScopedStorage(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (AGENCY_SCOPED_KEYS.includes(k) || AGENCY_SCOPED_PREFIXES.some((p) => k.startsWith(p)))) {
        doomed.push(k);
      }
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export function clearActiveMembershipId(email: string): void {
  try {
    localStorage.removeItem(key(email));
  } catch {
    // ignore
  }
}
